import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpHaningePage: ServicePageConfig = {
  slug: "flytthjalp-haninge",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Haninge – Handen till Dalarö | Ungdomskraft",
    description:
      "Boka flytthjälp i Haninge: Handen, Vega, Jordbro, Brandbergen, Vendelsö, Västerhaninge, Tungelsta och Dalarö. Skåpbil, bärare och fast paketpris.",
    h1: "Flytthjälp Haninge",
  },

  hero: {
    heading: "Flytthjälp i Haninge",
    intro:
      "Vi flyttar dig i hela Haninge – från lägenheten i Handen eller Jordbro till nybygget i Vega, villan i Vendelsö eller huset i Tungelsta och ut mot Dalarö. Bärare och skåpbil kommer till dörren, och paketpriset ser du redan när du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft bär flyttkartonger vid flytthjälp i Haninge",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Sedan 2014 har Ungdomskraft hjälpt hushåll i Stockholms län att flytta. Haninge är en kommun där man kan gå från höghus i Brandbergen till landsbygd bortom Västerhaninge på en kvart, och våra bärare planerar flytten efter just det.",
    bookYourText: "Boka flytt i Haninge",
    bookDescText:
      "Ange gamla och nya adressen, välj paket efter mängden och välj tid. Priset räknas ut direkt.",
    vehicleTitleText: "Bil och bärare, hela vägen",
    vehicleDescText:
      "Vi kommer med skåpbil och utrustning och kör på Nynäsvägen eller småvägarna ut mot kusten. Du behöver varken hyra bil eller släp.",
  },

  itemsSection: {
    heading: "Saker vi ofta flyttar i Haninge",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffor, divaner och fåtöljer, skyddade med filt",
        image: "/sofa.webp",
        alt: "Soffa som bärs ut vid flytt i Handen, Haninge",
      },
      {
        name: "Sängar & madrasser",
        description: "Sängar i alla storlekar, våningssängar och madrasser",
        image: "/bed.webp",
        alt: "Säng som flyttas från lägenhet i Jordbro, Haninge",
      },
      {
        name: "Bord & stolar",
        description: "Matbord, soffbord, trädgårdsbord och stolar",
        image: "/table.webp",
        alt: "Matbord som lastas vid villaflytt i Vendelsö, Haninge",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, frys, kyl och torktumlare",
        image: "/washer.webp",
        alt: "Frys som flyttas från hus i Tungelsta, Haninge",
      },
      {
        name: "Garderober & byråer",
        description: "Garderobsskåp, byråer och vitrinskåp",
        image: "/wardrobe.webp",
        alt: "Garderob som bärs vid flytt i Brandbergen, Haninge",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger från kök, förråd, garage och barnrum",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs in i nybyggd lägenhet i Vega, Haninge",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Haninge – höghus, nybyggen och hus på landet",
    mainText:
      "Haninge växte snabbt på 1960- och 70-talen när pendeltåget kom, och mycket av bebyggelsen från den tiden finns kvar: Brandbergen och Jordbro har stora flerbostadsområden, och kring Handen och Haninge centrum ligger kommunens tätaste kvarter. På senare år har Vega vuxit fram som ett nytt bostadsområde med egen pendeltågsstation. Samtidigt är stora delar av kommunen villor och hus på landet – Vendelsö, Västerhaninge, Tungelsta och Årsta havsbad – och längst ut ligger skärgårdssamhället Dalarö. En flytt i Haninge kan alltså betyda många trappor upp i ett hus med hiss eller en lång grusväg till ett hus med uthus, och vi frågar om båda vid bokningen.\n\nMånga som flyttar till Haninge kommer från Stockholms söderort, ofta för att få större bostad eller ett eget hus. Nynäsvägen (riksväg 73) är den självklara vägen, och flytten går oftast att köra på en tur om bilen lastas rätt. Inom kommunen är flytten från hyreslägenhet i Jordbro eller Brandbergen till radhus eller villa i Vendelsö eller Västerhaninge vanlig. Den som i stället lämnar ett hus brukar ha mycket i garage och förråd, så välj då ett av de större paketen.\n\nPendeltåget på Nynäsbanan stannar i Vega, Handen, Jordbro, Västerhaninge, Krigslida och Tungelsta, vilket gör att många bor stationsnära men utan lastplats vid porten. Vi tar reda på var bilen kan stå och bär därifrån. Dalarö når vi via Dalarövägen och Muskö via Muskötunneln. Öar utan vägförbindelse, som Utö och Ornö, kan vi inte köra till – där behövs båttransport som vi inte erbjuder.",
    preparationHeading: "Förbered flytten i Haninge",
    preparationText:
      "Packa i kartonger och skriv på vilket rum de ska till. Töm skåp och byråer, frosta av frysen dagen innan och plocka ner lampor och tavlor. Ta hand om det som bara ska slängas innan flyttdagen, eller boka bortforsling samtidigt, så betalar du inte för att flytta det.\n\nI Brandbergen, Jordbro och Handen: kolla om bilen får köra in på gården eller om det finns lastzon nära porten, och om hissen går att låsa. Flyttar du till eller från hus på landet i Tungelsta, Västerhaninge eller mot Dalarö, berätta om grusväg, backe eller grind så att vi planerar för det. Portkod och nycklar till båda adresserna gör att vi kan börja direkt.",
    additionalSections: [
      {
        heading: "Orter och områden vi flyttar i, i Haninge",
        content:
          "Vi flyttar i hela den del av Haninge kommun som går att nå med bil: Handen, Vega, Jordbro, Brandbergen, Vendelsö, Västerhaninge, Krigslida, Tungelsta, Årsta havsbad, Dalarö och Muskö.\n\nVi flyttar också mellan Haninge och grannkommunerna – Tyresö, Huddinge, Nynäshamn och Stockholm. Körsträcka inom 20 km ingår i paketpriset, och längre sträckor debiteras per kilometer. Du ser hela priset innan du bokar.",
      },
      {
        heading: "Hus på landet och sommarhus som blivit åretruntboende",
        content:
          "I södra Haninge och mot Dalarö finns många hus där tomten är stor och avståndet från vägen långt. En sådan flytt tar mer tid för bärarna, och ofta finns det både bod och garage att tömma. Beskriv tomten och vad som ska med när du bokar, så väljer vi rätt paket. Blir det saker över som inte ska med, kan vi köra dem till återvinning om du bokar bortforsling.",
        cta: { text: "Se bortforsling i Stockholm →", href: "/bortforsling-stockholm" },
      },
      {
        heading: "Varför boka flytthjälp i Haninge med Ungdomskraft?",
        listItems: [
          {
            label: "Vi planerar efter bostaden –",
            text: "Höghus i Brandbergen, nybygge i Vega och hus på landet i Tungelsta kräver olika upplägg. Vi frågar rätt saker från början.",
          },
          {
            label: "Tydligt pris –",
            text: "Paketpris och körsträcka syns innan du bokar – inget timpris som växer under dagen.",
          },
          {
            label: "RUT-avdrag på fakturan –",
            text: "Du betalar hälften av arbetskostnaden; resten sköter vi med Skatteverket.",
          },
          {
            label: "Försäkrat –",
            text: "Uppdragen är försäkrade genom Trygg Hansa.",
          },
          {
            label: "Ärligt om vad vi når –",
            text: "Vi kör till alla adresser med vägförbindelse. Öar som kräver båt tar vi inte, och det säger vi hellre direkt.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Haninge?",
      answer:
        "Du väljer paket efter hur mycket som ska flyttas: Liten 995 kr, Mellan 1 495 kr, Stor 2 495 kr, Extra Stor 4 995 kr (upp till 4 h) och Mega Stor 8 995 kr (upp till 8 h). Körsträcka inom 20 km ingår, längre sträckor räknas per kilometer. Priset syns direkt vid bokning.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Haninge?",
      answer:
        "Ja, du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år. Avdraget görs direkt på fakturan.",
    },
    {
      question: "Flyttar ni till Dalarö?",
      answer:
        "Ja, Dalarö nås med bil via Dalarövägen och ingår i vårt område. Eftersom det är längre bort kan körsträckan bli mer än de 20 km som ingår – du ser det i priset innan du bokar.",
    },
    {
      question: "Kan ni flytta till Utö eller Ornö?",
      answer:
        "Nej. Vi kör bara till adresser som går att nå på väg och erbjuder inte båttransport till öar utan vägförbindelse.",
    },
    {
      question: "Bär ni i höghusen i Brandbergen och Jordbro?",
      answer:
        "Ja. Ange våning och om det finns hiss när du bokar. Stora möbler som inte ryms i hissen bär vi i trappan.",
    },
    {
      question: "Flyttar ni från Stockholm till Haninge?",
      answer:
        "Ja, det är en av de vanligaste flyttarna vi gör i kommunen. Priset beror på paket och körsträcka och visas när du fyllt i adresserna.",
    },
    {
      question: "Hur snabbt kan ni komma?",
      answer:
        "Ofta inom några dagar. I bokningen ser du lediga tider för dina adresser.",
    },
    {
      question: "Ingår skåpbil?",
      answer:
        "Ja, alla paket från Liten och uppåt har två bärare och skåpbil.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, kyl, frys, tvättmaskin och torktumlare. Frosta av frysen och töm kylen dagen innan.",
    },
    {
      question: "Tar ni isär möbler?",
      answer:
        "Ja, vi tar isär och sätter ihop sängar, garderober och bord som behöver det.",
    },
    {
      question: "Flyttar ni på helger?",
      answer:
        "Ja, alla dagar i veckan.",
    },
    {
      question: "Kan ni tömma garage och förråd i samma flytt?",
      answer:
        "Ja, det som ska med till nya adressen bär vi från garage, bod och förråd också. Välj ett paket som räcker till allt.",
    },
    {
      question: "Vilka delar av Haninge täcker ni?",
      answer:
        "Handen, Vega, Jordbro, Brandbergen, Vendelsö, Västerhaninge, Krigslida, Tungelsta, Årsta havsbad, Dalarö och Muskö – alla adresser som nås med bil.",
    },
    {
      question: "Vilka kommuner utanför Haninge flyttar ni i?",
      answer: `Vi flyttar i hela Stockholmsregionen: ${stockholmKommunerText}. Prismodellen är densamma överallt – paketpris plus körsträcka över 20 km.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Haninge i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbil, bär ut och lastar så att allt står stadigt under resan. Sedan kör vi till nya adressen, i Haninge eller vidare.",
    step3Title: "Klart i nya hemmet",
    step3Desc:
      "Vi bär in allt, ställer möblerna där du vill och sätter ihop det som tagits isär.",
    ctaHeading: "Boka flytthjälp i Haninge",
    ctaText:
      "Fyll i adresserna, välj paket och tid. Priset syns direkt, med bil och bärare.",
  },

  en: {
    slug: "moving-help-haninge",
    meta: {
      title: "Moving Help Haninge – Handen to Dalarö | Ungdomskraft",
      description:
        "Book moving help in Haninge: Handen, Vega, Jordbro, Brandbergen, Vendelsö, Västerhaninge, Tungelsta and Dalarö. Van, carriers and fixed package price.",
      h1: "Moving Help Haninge",
    },
    hero: {
      heading: "Moving Help in Haninge",
      intro:
        "We move you anywhere in Haninge – from the flat in Handen or Jordbro to the new build in Vega, the villa in Vendelsö or the house in Tungelsta and out towards Dalarö. Carriers and a van come to the door, and you see the package price when you book.",
      heroImageAlt: "Ungdomskraft carrying moving boxes in Haninge",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Since 2014 Ungdomskraft has helped households in Stockholm County move. Haninge is a municipality where you can go from a tower block in Brandbergen to countryside beyond Västerhaninge in a quarter of an hour, and our carriers plan the move around exactly that.",
      bookYourText: "Book a move in Haninge",
      bookDescText:
        "Enter the old and new address, choose a package by volume and pick a time. The price is calculated straight away.",
      vehicleTitleText: "Van and carriers, all the way",
      vehicleDescText:
        "We bring a van and equipment and drive Nynäsvägen or the smaller roads out towards the coast. You don't need to rent a van or trailer.",
    },
    itemsSection: {
      heading: "Items we often move in Haninge",
      items: [
        { name: "Sofas & armchairs", description: "Sofas, daybeds and armchairs, protected with blankets", image: "/sofa.webp", alt: "Sofa carried out during a move in Handen, Haninge" },
        { name: "Beds & mattresses", description: "Beds of all sizes, bunk beds and mattresses", image: "/bed.webp", alt: "Bed moved from a flat in Jordbro, Haninge" },
        { name: "Tables & chairs", description: "Dining tables, coffee tables, garden tables and chairs", image: "/table.webp", alt: "Dining table loaded during a villa move in Vendelsö, Haninge" },
        { name: "Appliances", description: "Washing machine, freezer, fridge and tumble dryer", image: "/washer.webp", alt: "Freezer moved from a house in Tungelsta, Haninge" },
        { name: "Wardrobes & dressers", description: "Wardrobes, dressers and display cabinets", image: "/wardrobe.webp", alt: "Wardrobe carried during a move in Brandbergen, Haninge" },
        { name: "Moving boxes", description: "Boxes from the kitchen, storage, garage and kids' rooms", image: "/box.webp", alt: "Moving boxes carried into a new flat in Vega, Haninge" },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Haninge – tower blocks, new builds and country houses",
      mainText:
        "Haninge grew fast in the 1960s and 70s when the commuter train arrived, and much of the housing from that time remains: Brandbergen and Jordbro have large apartment estates, and around Handen and Haninge centrum are the municipality's densest blocks. In recent years Vega has grown into a new residential area with its own commuter-rail station. At the same time, large parts of the municipality are villas and country houses – Vendelsö, Västerhaninge, Tungelsta and Årsta havsbad – and furthest out is the archipelago village of Dalarö. A move in Haninge can mean several floors up in a building with a lift or a long gravel road to a house with outbuildings, and we ask about both when you book.\n\nMany people moving to Haninge come from Stockholm's southern suburbs, often for a bigger home or a house of their own. Nynäsvägen (national road 73) is the obvious route, and the move can usually be done in one trip if the van is loaded right. Within the municipality, moving from a rental flat in Jordbro or Brandbergen to a terraced house or villa in Vendelsö or Västerhaninge is common. Those leaving a house instead usually have a lot in the garage and storage, so pick one of the larger packages.\n\nCommuter trains on the Nynäs line stop at Vega, Handen, Jordbro, Västerhaninge, Krigslida and Tungelsta, so many people live near a station but without a loading spot at the door. We find out where the van can stand and carry from there. We reach Dalarö via Dalarövägen and Muskö via the Muskö tunnel. Islands without a road link, such as Utö and Ornö, we cannot drive to – they need boat transport, which we don't offer.",
      preparationHeading: "Prepare your move in Haninge",
      preparationText:
        "Pack into boxes and write which room they go to. Empty cupboards and dressers, defrost the freezer the day before and take down lamps and pictures. Deal with things that are just going to be thrown away before moving day, or book junk removal at the same time, so you don't pay to move them.\n\nIn Brandbergen, Jordbro and Handen, check whether the van may drive into the courtyard or whether there is a loading zone near the entrance, and whether the lift can be locked. If you are moving to or from a country house in Tungelsta, Västerhaninge or towards Dalarö, tell us about gravel roads, slopes or gates so we can plan for it. Door codes and keys for both addresses let us start straight away.",
      additionalSections: [
        {
          heading: "Places and areas we move in, in Haninge",
          content:
            "We move across the part of Haninge Municipality that can be reached by car: Handen, Vega, Jordbro, Brandbergen, Vendelsö, Västerhaninge, Krigslida, Tungelsta, Årsta havsbad, Dalarö and Muskö.\n\nWe also move between Haninge and neighbouring municipalities – Tyresö, Huddinge, Nynäshamn and Stockholm. Driving within 20 km is included in the package price, and longer distances are charged per kilometre. You see the full price before you book.",
        },
        {
          heading: "Country houses and summer homes turned year-round",
          content:
            "In southern Haninge and towards Dalarö there are many houses on large plots, far from the road. Such a move takes more time for the carriers, and there is often both a shed and a garage to clear. Describe the plot and what's coming along when you book, and we'll pick the right package. If there are leftovers that aren't coming, we can take them to recycling if you book junk removal.",
          cta: { text: "See junk removal in Stockholm →", href: "/en/junk-removal-stockholm" },
        },
        {
          heading: "Why book moving help in Haninge with Ungdomskraft?",
          listItems: [
            { label: "We plan around the home –", text: "A tower block in Brandbergen, a new build in Vega and a country house in Tungelsta need different set-ups. We ask the right things from the start." },
            { label: "Clear price –", text: "Package price and driving distance show before you book – no hourly rate growing through the day." },
            { label: "RUT deduction on the invoice –", text: "You pay half the labour cost; we handle the rest with the Swedish Tax Agency." },
            { label: "Insured –", text: "Jobs are insured through Trygg Hansa." },
            { label: "Honest about our reach –", text: "We drive to every address with a road link. Islands that need a boat we don't take, and we'd rather say so up front." },
          ],
        },
      ],
    },
    faq: [
      { question: "How much does moving help in Haninge cost?", answer: "You choose a package by how much is moving: Small SEK 995, Medium SEK 1,495, Large SEK 2,495, Extra Large SEK 4,995 (up to 4 h) and Mega Large SEK 8,995 (up to 8 h). Driving within 20 km is included; longer distances are charged per kilometre. The price shows when you book." },
      { question: "Can I get a RUT deduction on moving help in Haninge?", answer: "Yes, you get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. It is applied directly on the invoice." },
      { question: "Do you move to Dalarö?", answer: "Yes, Dalarö can be reached by car via Dalarövägen and is within our area. As it is further out, the driving distance may exceed the 20 km included – you see it in the price before you book." },
      { question: "Can you move me to Utö or Ornö?", answer: "No. We only drive to addresses reachable by road and don't offer boat transport to islands without a road link." },
      { question: "Do you carry in the tower blocks of Brandbergen and Jordbro?", answer: "Yes. State the floor and whether there is a lift when booking. Large furniture that won't fit in the lift we carry by the stairs." },
      { question: "Do you move people from Stockholm to Haninge?", answer: "Yes, it is one of the most common moves we do in the municipality. The price depends on package and distance and is shown once you have entered the addresses." },
      { question: "How soon can you come?", answer: "Often within a few days. The booking shows free times for your addresses." },
      { question: "Is a van included?", answer: "Yes, every package from Small upwards has two carriers and a van." },
      { question: "Do you move appliances?", answer: "Yes – fridge, freezer, washing machine and tumble dryer. Defrost the freezer and empty the fridge the day before." },
      { question: "Do you take furniture apart?", answer: "Yes, we take apart and reassemble beds, wardrobes and tables that need it." },
      { question: "Do you move at weekends?", answer: "Yes, every day of the week." },
      { question: "Can you clear the garage and storage in the same move?", answer: "Yes, whatever is coming to the new address we also carry from the garage, shed and storage. Pick a package that covers it all." },
      { question: "Which parts of Haninge do you cover?", answer: "Handen, Vega, Jordbro, Brandbergen, Vendelsö, Västerhaninge, Krigslida, Tungelsta, Årsta havsbad, Dalarö and Muskö – every address reachable by car." },
      { question: "Which municipalities outside Haninge do you serve?", answer: `We move across the Stockholm region: ${stockholmKommunerText}. The pricing is the same everywhere – package price plus driving beyond 20 km.` },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Haninge in 3 steps",
      step2Text:
        "The carriers arrive with a van, carry out and load so everything stays put on the way. Then we drive to the new address, in Haninge or beyond.",
      step3Title: "Done in the new home",
      step3Desc:
        "We carry everything in, place the furniture where you want it and reassemble what was taken apart.",
      ctaHeading: "Book moving help in Haninge",
      ctaText:
        "Enter the addresses, choose a package and time. The price shows straight away, with van and carriers.",
    },
  },
};
